<template>
    <div class="relative" :class="rootClass" :style="rootStyle" v-bind="nonEventAttrs">
        <input
            data-testid="activix-masked-input-input"
            class="form-control"
            :class="classes"
            :disabled="disabled"
            :inputmode="inputMode"
            :maxlength="maxLength"
            :placeholder="placeholder"
            :type="type"
            :value="currentValue"
            ref="input"
            v-on="inputListeners"
            v-imask="maskOptions"
        />
        <div
            class="absolute h-full flex items-center justify-center left-0 top-0 w-12 pointer-events-none"
            v-if="$slots.icon"
        >
            <slot name="icon" />
        </div>
    </div>
</template>

<script>
    import IMask from 'imask';
    import { IMaskDirective } from 'vue-imask';
    import i18n from '@/plugins/vue-i18n.js';
    import { splitAttrs } from '@/utils/vue-related.ts';

    export default {
        name: 'ActivixMaskedInput',

        // FIXME(VUE3): Remove once compat mode is removed; real Vue 3 merges listeners into $attrs by default.
        compatConfig: { MODE: 3 },

        directives: {
            imask: IMaskDirective,
        },

        inheritAttrs: false,

        emits: ['input', 'update:modelValue'],

        props: {
            inputClass: {
                type: [String, Array],
                default: '',
            },
            type: {
                type: String,
                default: 'text',
                validator: (value) =>
                    ['text', 'tel', 'url', 'password', 'search', 'number', 'email'].includes(value),
            },
            placeholder: {
                type: String,
                default: '',
            },
            inputMode: {
                type: String,
                default: 'text',
            },
            maxLength: {
                type: Number,
                default: null,
            },
            value: {
                type: [String, Number],
                default: '',
            },
            modelValue: {
                type: [String, Number],
                default: undefined,
            },
            maskType: {
                type: String,
                required: true,
                validator: (value) =>
                    [
                        'phone',
                        'phoneWithCountry',
                        'phoneExtension',
                        'postalCode',
                        'email',
                        'number',
                        'money',
                        'percent',
                        'kilometers',
                        'custom',
                        'text',
                    ].includes(value),
            },
            customMask: {
                type: [Array, Function, Boolean, Object],
                default: () => null,
            },
            disabled: {
                type: Boolean,
                default: false,
            },
            hasError: {
                type: Boolean,
                default: false,
            },
            integerLimit: {
                type: Number,
                default: 10,
            },
            allowLeadingZeroes: {
                type: Boolean,
                default: false,
            },
            allowZeroValue: {
                type: Boolean,
                default: false,
            },
            minimumValue: {
                type: Number,
                default: null,
            },
            white: {
                type: Boolean,
                default: false,
            },
            country: {
                type: String,
                default: 'CA',
            },
        },

        data() {
            return {
                currentValue: '',
                isSyncingFromProp: false,
                isSyncingFromMaskChange: false,
                maskChangeSequence: 0,
            };
        },

        computed: {
            classes() {
                const inputClass = Array.isArray(this.inputClass)
                    ? this.inputClass
                    : [this.inputClass];

                return inputClass.concat([
                    {
                        'has-error': this.hasError,
                        'pl-12': !!this.$slots.icon,
                        'input-white': this.white,
                    },
                ]);
            },

            maskOptions() {
                switch (this.maskType) {
                    case 'phone':
                        return {
                            lazy: true,
                            mask: '#00 000-0000',
                            definitions: {
                                '#': /[1-9]/,
                            },
                        };

                    case 'phoneWithCountry':
                        return {
                            lazy: true,
                            mask: '{+1} #00 000-0000',
                            definitions: {
                                '#': /[1-9]/,
                            },
                        };

                    case 'phoneExtension':
                        return {
                            lazy: true,
                            mask: 'aaaaaaaaaa',
                            definitions: {
                                a: /[\dw]/,
                            },
                        };

                    case 'postalCode':
                        if (this.country == 'CA') {
                            return {
                                lazy: true,
                                mask: 'a0a 0a0',
                            };
                        }

                        if (this.country == 'US') {
                            return {
                                lazy: true,
                                mask: '00000',
                            };
                        }

                        return null;

                    case 'email':
                        return {
                            lazy: true,
                            // Keep typing permissive enough for partial addresses while
                            // rejecting whitespace and multiple @ signs.
                            mask: /^[^\s@]*(@[A-Za-z0-9.-]*)?$/,
                        };

                    case 'number':
                        return this.getNumberMaskOptions({
                            allowDecimal: false,
                            allowNegative: false,
                            scale: 0,
                        });

                    case 'money':
                        return {
                            eager: true,
                            mask:
                                i18n.getContentLanguage() === 'fr'
                                    ? `num${this.localizedMaskOptions.moneySuffix}`
                                    : `${this.localizedMaskOptions.moneyPrefix}num`,
                            blocks: {
                                num: this.getNumberMaskOptions({
                                    allowDecimal: true,
                                    allowNegative: true,
                                    scale: 2,
                                }),
                            },
                        };

                    case 'percent':
                        return {
                            eager: true,
                            mask: 'num %',
                            blocks: {
                                num: {
                                    ...this.getNumberMaskOptions({
                                        allowDecimal: true,
                                        allowNegative: false,
                                        scale: 2,
                                    }),
                                    max: 100,
                                    padFractionalZeros: true,
                                },
                            },
                        };

                    case 'kilometers':
                        return this.getNumberMaskOptions({
                            allowDecimal: false,
                            allowNegative: false,
                            scale: 0,
                        });

                    case 'text':
                        return null;

                    default:
                        return this.getCustomMaskOptions();
                }
            },

            hasMask() {
                return !!this.maskOptions;
            },

            localizedMaskOptions() {
                const defaults = {
                    integerLimit: this.integerLimit,
                    allowLeadingZeroes: this.allowLeadingZeroes || this.allowZeroValue,
                };

                if (i18n.getContentLanguage() === 'fr') {
                    return {
                        ...defaults,
                        moneyPrefix: '',
                        moneySuffix: ' $',
                        includeThousandsSeparator: false,
                    };
                }

                return {
                    ...defaults,
                    moneyPrefix: '$',
                    moneySuffix: '',
                };
            },

            nonEventAttrs() {
                // FIXME(VUE3): Remove splitAttrs after full Vue 3 migration
                const nonEventAttrs = { ...splitAttrs(this.$attrs).nonEventAttrs };

                // Vue 2 magically applied class/style to the root element even with
                // inheritAttrs: false. Vue 3 doesn't — they're just regular $attrs now.
                // Since this component routes attrs to different children (class/style
                // to the wrapper <div>, events to the <input>), we strip them here and
                // bind them manually via :class="rootClass" / :style="rootStyle".
                delete nonEventAttrs.class;
                delete nonEventAttrs.style;

                return nonEventAttrs;
            },

            resolvedValue() {
                return this.modelValue !== undefined ? this.modelValue : this.value;
            },

            rootClass() {
                return this.$attrs.class;
            },

            rootStyle() {
                return this.$attrs.style;
            },

            inputListeners() {
                return {
                    ...splitAttrs(this.$attrs).listeners,
                    accept: this.onAccept,
                    input: this.onInput,
                };
            },
        },

        watch: {
            resolvedValue() {
                this.updateValue();
            },
            maskOptions() {
                this.suppressMaskAcceptDuringReconfiguration();
            },
        },

        methods: {
            getNormalizedValue() {
                let value = this.resolvedValue;

                if (!value && (isNaN(parseInt(value, 10)) || !this.allowZeroValue)) {
                    value = '';
                }

                return value.toString();
            },

            suppressMaskAcceptDuringReconfiguration() {
                const currentSequence = this.maskChangeSequence + 1;
                this.maskChangeSequence = currentSequence;
                this.isSyncingFromMaskChange = true;

                this.$nextTick(() => {
                    if (this.maskType === 'money') {
                        const inputElement = this.getInputElement();
                        const normalizedValue = this.getNormalizedValue();

                        if (this.hasMask && inputElement?.maskRef) {
                            const remaskedValue = IMask.createMask(this.maskOptions);
                            remaskedValue.resolve(normalizedValue);
                            inputElement.maskRef.value = remaskedValue.value;
                        } else if (inputElement) {
                            inputElement.value = normalizedValue;
                        }
                    }

                    this.$nextTick(() => {
                        if (this.maskChangeSequence === currentSequence) {
                            this.isSyncingFromMaskChange = false;
                        }
                    });
                });
            },

            getNumberMaskOptions({ allowDecimal, allowNegative, scale }) {
                return {
                    lazy: true,
                    mask: Number,
                    scale,
                    signed: allowNegative,
                    padFractionalZeros: false,
                    normalizeZeros: !this.localizedMaskOptions.allowLeadingZeroes,
                    radix: '.',
                    mapToRadix: ['.', ','],
                    thousandsSeparator: '',
                    validate: (_, masked) => {
                        const unmaskedValue = (masked.unmaskedValue || '').replace(/^[-+]/, '');
                        const integerPart = unmaskedValue.split(/[.,]/)[0];

                        return integerPart.length <= this.localizedMaskOptions.integerLimit;
                    },
                    ...(allowDecimal ? {} : { scale: 0 }),
                };
            },

            getCustomMaskOptions() {
                const customMask =
                    typeof this.customMask === 'function'
                        ? this.customMask(this.currentValue)
                        : this.customMask;

                if (Array.isArray(customMask)) {
                    return this.convertTextMaskArrayToImask(customMask);
                }

                if (customMask && typeof customMask === 'object') {
                    if (Array.isArray(customMask.mask)) {
                        const convertedMask = this.convertTextMaskArrayToImask(customMask.mask);
                        if (!convertedMask) {
                            return null;
                        }

                        return {
                            ...customMask,
                            ...convertedMask,
                            definitions: {
                                ...(customMask.definitions || {}),
                                ...(convertedMask.definitions || {}),
                            },
                        };
                    }

                    return customMask;
                }

                return null;
            },

            convertTextMaskArrayToImask(mask) {
                if (!Array.isArray(mask) || !mask.length) {
                    return null;
                }

                const definitionKeys = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
                const definitions = {};
                const regexKeyMap = new Map();
                let definitionIndex = 0;
                const pattern = mask
                    .map((token) => {
                        if (token instanceof RegExp) {
                            const regexSignature = `${token.source}/${token.flags}`;
                            if (!regexKeyMap.has(regexSignature)) {
                                const nextKey =
                                    definitionKeys[definitionIndex] ||
                                    definitionKeys[definitionKeys.length - 1];
                                regexKeyMap.set(regexSignature, nextKey);
                                definitions[nextKey] = token;
                                definitionIndex += 1;
                            }

                            return regexKeyMap.get(regexSignature);
                        }

                        if (typeof token === 'string') {
                            const escapedToken = token.replace(/[{}]/g, '\\$&');
                            return `{${escapedToken}}`;
                        }

                        return '';
                    })
                    .join('');

                if (!pattern) {
                    return null;
                }

                return {
                    lazy: true,
                    mask: pattern,
                    definitions,
                };
            },

            getInputElement() {
                const input = this.$refs.input;
                if (!input) {
                    return null;
                }

                return input.$el || input;
            },

            updateValue() {
                const normalizedValue = this.getNormalizedValue();

                this.currentValue = normalizedValue;

                this.$nextTick(() => {
                    const inputElement = this.getInputElement();
                    if (!inputElement) {
                        return;
                    }

                    this.isSyncingFromProp = true;

                    if (this.hasMask && inputElement.maskRef) {
                        inputElement.maskRef.value = normalizedValue;
                    } else {
                        inputElement.value = normalizedValue;
                    }

                    this.$nextTick(() => {
                        this.isSyncingFromProp = false;
                    });
                });
            },

            focus() {
                this.$nextTick(() => {
                    const inputElement = this.$refs.input.$el || this.$refs.input;
                    inputElement.focus();
                    inputElement.select();
                });
            },

            getRawValue(value) {
                if (['money'].includes(this.maskType)) {
                    value = parseFloat(value.replace(/[, $]/g, ''));

                    if (Number.isNaN(value)) {
                        value = null;
                    }
                }

                if (['number'].includes(this.maskType)) {
                    value = parseInt(value.replace(/[, $]/g, ''), 10);

                    if (isNaN(value)) {
                        value = null;
                    }
                }

                if (['phone', 'phoneWithCountry'].includes(this.maskType)) {
                    value = value.replace(/[^\d+]/g, '');
                }

                return value;
            },

            emitInput(value) {
                const rawValue = this.getRawValue(value);

                if (this.resolvedValue == rawValue) {
                    return;
                }

                this.$emit('input', rawValue);
                this.$emit('update:modelValue', rawValue);
            },

            onAccept(event) {
                if (!this.hasMask || this.isSyncingFromProp || this.isSyncingFromMaskChange) {
                    return;
                }

                // Preserve legacy behavior for formatted masks (e.g. CA postal code with space).
                const acceptValue = event?.detail?.value ?? event?.detail?.unmaskedValue ?? '';
                this.emitInput(acceptValue);
            },

            onInput(event) {
                if (this.hasMask || this.isSyncingFromProp) {
                    return;
                }

                this.emitInput(event?.target?.value || '');
            },
        },

        mounted() {
            this.$nextTick(() => {
                this.updateValue();
            });
        },
    };
</script>
